import { Alert, Badge, Button, Card, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchClientPortalDelivery } from '../api/clientPortalApi';
import { ClientPortalLayout } from '../components/ClientPortalLayout';
import { BackButton } from '../components/BackButton';
import { formatCurrency, formatDate, formatMoveState } from '../utils/formatters';
import React from "react";

function TrackingCell({ trackingRef, trackingUrl }) {
  if (!trackingUrl) {
    return trackingRef || 'Not available';
  }

  return (
    <a href={trackingUrl} target="_blank" rel="noreferrer">
      {trackingRef || '[Tracking link]'}
    </a>
  );
}

function IncomingShipmentCell({ shipments, deliveryId, moveId }) {
  if (!shipments.length) {
    return '-';
  }

  const firstShipment = shipments[0];

  return (
    <OverlayTrigger
      placement="top"
      overlay={(
        <Tooltip id={`${deliveryId}-${moveId}-incoming-tooltip`}>
          {shipments.map((shipment) => (
            <div key={`${shipment.date}-${shipment.destination}`}>
              {formatDate(shipment.date)} {shipment.quantity} pcs -> {shipment.destination}
            </div>
          ))}
        </Tooltip>
      )}
    >
      <span className="client-portal-tooltip-link">
        {formatDate(firstShipment.date)}
      </span>
    </OverlayTrigger>
  );
}

export function ClientPortalDeliveryPage() {
  const { "*": deliveryName } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['client-portal-delivery', deliveryName],
    queryFn: fetchClientPortalDelivery,
  });

  const delivery = data?.result;

  return (
    <ClientPortalLayout>
      <div className="container">
        {isLoading ? (
          <div className="page-container text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading delivery details...</p>
          </div>
        ) : isError ? (
          <div className="page-container">
            <Alert variant="danger">Error loading delivery: {error.message}</Alert>
          </div>
        ) : !delivery ? (
          <div className="page-container">
            <Alert variant="warning">Delivery not found.</Alert>
          </div>
        ) : (
          <>
            <div className="pt-3">
              <BackButton fallbackUrl="/client/deliveries" className="mb-2" />
            </div>
            <div className="row mb-4">
              <div className="col-12">
                <h2>
                  {delivery.name}{' '}
                  <Badge bg={
                    delivery.state === 'done' ? 'success' :
                    delivery.state === 'assigned' ? 'info' :
                    'secondary'
                  }>
                    {formatMoveState(delivery.state)}
                  </Badge>
                </h2>
              </div>
            </div>
            <div className="row">
              <div className="col-3">
                <Card>
                  <Card.Header>Basic info</Card.Header>
                  <Card.Body>
                    <p>
                      Sales order:{' '}
                      <b>
                        {delivery.sales_order ? (
                          <Link to={`/client/orders/${delivery.sales_order.name}`}>
                            {delivery.sales_order.name}
                          </Link>
                        ) : '-'}
                      </b>
                    </p>
                    <p>Tracking: <b><TrackingCell trackingRef={delivery.carrier_tracking_ref === 'False' ? null : delivery.carrier_tracking_ref} trackingUrl={delivery.carrier_tracking_url_manual === 'False' ? null : delivery.carrier_tracking_url_manual} /></b></p>
                    <p>Company: <b>{delivery.partner?.name || '-'}</b></p>
                    <p>Scheduled date: <b>{formatDate(delivery.scheduled_date)}</b></p>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-9">
                <Card>
                  <Card.Header>Products</Card.Header>
                  <Table responsive striped className="card-body table product-table mb-0">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Status</th>
                        <th>ETA</th>
                        <th>Total price</th>
                        <th>Total qty</th>
                        <th>Reserved</th>
                        <th>On backorder</th>
                      </tr>
                    </thead>
                    <tbody>
                      {delivery.moves.map((move) => {
                        const productNameMatch = move.product_id?.name?.match(/^\[(.*?)\]\s*(.*)$/);
                        const sku = productNameMatch ? productNameMatch[1] : '';
                        const productName = productNameMatch ? productNameMatch[2] : move.product_id?.name;
                        let eta = move.eta;
                        if (move.state === 'assigned') {
                          eta = delivery.scheduled_date;
                        }

                        return (
                          <tr key={move.name}>
                            <th scope="row">{sku || '-'}</th>
                            <td>{productName || '-'}</td>
                            <td>{formatMoveState(move.state)}</td>
                            <td>{formatDate(eta)}</td>
                            <td>{formatCurrency(move.so_line_total_price)}</td>
                            <td>{move.product_uom_qty}</td>
                            <td>{move.quantity}</td>
                            <td>{Math.max(0, move.product_uom_qty - move.quantity)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card>
              </div>
            </div>
            {delivery.note ? (
              <div className="row my-2">
                <div className="col-12">
                  <Card>
                    <Card.Header as="h5">Note</Card.Header>
                    <Card.Body>
                      <div className="sale-note" dangerouslySetInnerHTML={{ __html: delivery.note }} />
                    </Card.Body>
                  </Card>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </ClientPortalLayout>
  );
}
